import React from 'react';
import { Sparkles } from 'lucide-react';
import { fitLevel, FIT_LABEL, FIT_TEXT, FIT_PILL } from '../../lib/fit';

interface FitBadgeProps {
  score: number;
  /** Show the Strong/Moderate/Weak pill next to the number. */
  showLabel?: boolean;
  /** Number font size in px (default 16, matching the card comp). */
  numberSize?: number;
  /** Mark the score as AI-derived (capture-analyst verdict supersedes the rule-based score). */
  aiScored?: boolean;
  className?: string;
}

/**
 * Fit badge — the BidWin Score number (Roboto Mono) paired with a
 * Strong/Moderate/Weak pill from the fit ramp. Replaces the single orange
 * score badge: a 49 and a 65 no longer look equally urgent. Color is never the
 * sole signal — the number and the text label both carry the meaning.
 */
export function FitBadge({ score, showLabel = true, numberSize = 16, aiScored = false, className = '' }: FitBadgeProps) {
  const level = fitLevel(score);
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <span
        className={`font-mono font-medium leading-none ${FIT_TEXT[level]} inline-flex items-center gap-0.5`}
        style={{ fontSize: numberSize }}
        title={aiScored ? 'AI capture-analyst fit score' : undefined}
      >
        {aiScored && <Sparkles className="text-bw-primary" style={{ width: numberSize * 0.6, height: numberSize * 0.6 }} aria-label="AI-scored" />}
        {Math.round(score)}
      </span>
      {showLabel && (
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${FIT_PILL[level]}`}
        >
          {FIT_LABEL[level]}
        </span>
      )}
    </div>
  );
}

export default FitBadge;
